/**
 * What the sheet offers once you have scrolled past the seasons.
 *
 * Both actions are deliberately quiet — two text links on one line. This is
 * the foot of a sheet whose job is marking episodes watched, and the two
 * rarest actions on the screen had the loudest treatments on it.
 *
 * Setting a show aside moves it out of the queue without leaving the library:
 * the middle ground between dropping something you might come back to and
 * leaving it at the top of Up Next competing with what you actually watch.
 * Local to this device — Simkl has no status meaning "not right now", and
 * borrowing `hold` would change what the account says to every other client.
 */
import { useState } from "react";

import type { LibraryShow } from "@/api/schemas";
import type { OutboxAction } from "@/store/outbox";

/**
 * Removing a show erases its watched history at Simkl and cannot be undone
 * from here, so the destructive label only appears after an explicit step.
 *
 * Quiet by default. It sits at the foot of a sheet whose job is watching
 * episodes; a filled full-width button gave the rarest action on the screen
 * the loudest treatment on it. It turns red once it is asking to be confirmed,
 * which is the moment the warning is actually worth carrying.
 */
function RemoveShow({
  title,
  setAside,
  onToggleAside,
  onRemove,
}: {
  title: string;
  setAside: boolean;
  onToggleAside: () => void;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <div className="sheet-foot">
        <div className="foot-links">
          <button type="button" className="foot-link" onClick={onToggleAside}>
            {setAside ? "Back to Up Next" : "Set aside"}
          </button>
          <span className="foot-dot" aria-hidden="true" />
          <button
            type="button"
            className="foot-link"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Remove from library
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="sheet-foot confirming">
      <p className="sheet-warn">
        Removes {title} and its watched history from Simkl. This cannot be undone.
      </p>
      <div className="sheet-foot-row">
        <button
          type="button"
          className="quiet"
          onClick={() => {
            setConfirming(false);
          }}
        >
          Cancel
        </button>
        <button type="button" className="danger" onClick={onRemove}>
          Remove
        </button>
      </div>
    </div>
  );
}

/** Which footer the sheet gets: tracked shows can be removed, others added. */
export function Foot({
  show,
  inLibrary,
  setAside,
  onAction,
  onAdd,
  onToggleAside,
  onClose,
}: {
  show: LibraryShow;
  inLibrary: boolean;
  setAside: boolean;
  onAction: (action: OutboxAction) => void;
  onAdd: () => void;
  onToggleAside: () => void;
  onClose: () => void;
}) {
  if (!inLibrary) return <AddShow onAdd={onAdd} />;
  return (
    <RemoveShow
      title={show.show.title}
      setAside={setAside}
      onToggleAside={onToggleAside}
      onRemove={() => {
        onAction({ kind: "remove", simklId: show.show.ids.simkl });
        onClose();
      }}
    />
  );
}

/** The same slot for a show that is not in the library yet. */
function AddShow({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="sheet-foot">
      <button type="button" className="foot-add" onClick={onAdd}>
        Add to library
      </button>
    </div>
  );
}
